"use client";

import { useToast } from "@/components/providers/FetchToastProvider";
import { setActiveOrg } from "@/lib/enterprise/active-org-actions";
import { isEventScopedRoute, type ActiveOrgSummary } from "@/lib/enterprise/active-org-types";
import { ORG_EVENTS_PATH } from "@/lib/routes/enterprise-nav-paths";
import { usePathname, useRouter } from "next/navigation";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

/**
 * Active-organization scope for /enterprise. Surfaces outside the provider get a
 * defensive empty value via `useActiveOrg`, so non-enterprise pages don't crash.
 */
interface ActiveOrgContextValue {
  /** The org currently in effect, or null (0-org users / outside provider). */
  activeOrg: ActiveOrgSummary | null;
  /** Every org the user belongs to (admin or member). */
  orgs: ActiveOrgSummary[];
  /** True when the user is an admin of the active org. */
  isAdminOfActiveOrg: boolean;
  /** Switch the active org: persists the cookie, then re-scopes the app. */
  switchOrg: (orgId: number) => Promise<void>;
  /** True while a switch is in flight. */
  isSwitching: boolean;
}

const ActiveOrgContext = createContext<ActiveOrgContextValue | null>(null);

const DEFENSIVE_VALUE: ActiveOrgContextValue = {
  activeOrg: null,
  orgs: [],
  isAdminOfActiveOrg: false,
  switchOrg: async () => {},
  isSwitching: false,
};

interface ActiveOrgProviderProps {
  orgs: ActiveOrgSummary[];
  activeOrgId: number | null;
  children: ReactNode;
}

/**
 * Provides the active org + switcher to the enterprise shell. The active org is
 * derived from the server-provided `activeOrgId` each render; switching persists
 * the cookie via `setActiveOrg` and then `router.refresh()`s so all server
 * components re-scope. If the user is on an event route (whose event may belong
 * to the old org), navigates to the org-wide Events list instead of refreshing in place.
 *
 * @param props.orgs - All orgs the user belongs to.
 * @param props.activeOrgId - The server-resolved active org id (null for 0-org).
 * @param props.children - The enterprise shell subtree.
 * @returns The provider wrapping `children`.
 */
export const ActiveOrgProvider = ({ orgs, activeOrgId, children }: ActiveOrgProviderProps) => {
  // Hooks
  const router = useRouter();
  const pathname = usePathname();
  const { showToast } = useToast();

  // States
  const [isSwitching, setIsSwitching] = useState(false);

  // Derived values
  const activeOrg = useMemo(() => orgs.find((o) => o.id === activeOrgId) ?? null, [orgs, activeOrgId]);

  // Handlers
  const switchOrg = useCallback(
    async (orgId: number) => {
      if (orgId === activeOrgId || isSwitching) return;
      setIsSwitching(true);
      try {
        const result = await setActiveOrg(orgId, { explicit: true });
        if (!result.success) {
          showToast(result.error, "error");
          return;
        }
        const target = orgs.find((o) => o.id === orgId);
        showToast(`Switched to ${target?.name ?? "organization"}`, "success");
        if (isEventScopedRoute(pathname)) {
          router.push(ORG_EVENTS_PATH);
        } else {
          router.refresh();
        }
      } finally {
        setIsSwitching(false);
      }
    },
    [activeOrgId, isSwitching, orgs, pathname, router, showToast]
  );

  // Render
  const value = useMemo<ActiveOrgContextValue>(
    () => ({
      activeOrg,
      orgs,
      isAdminOfActiveOrg: activeOrg?.isAdmin ?? false,
      switchOrg,
      isSwitching,
    }),
    [activeOrg, orgs, switchOrg, isSwitching]
  );

  return <ActiveOrgContext.Provider value={value}>{children}</ActiveOrgContext.Provider>;
};

/**
 * Active-org context, or a defensive empty value when outside the provider.
 *
 * @returns The active-org context value.
 */
export function useActiveOrg(): ActiveOrgContextValue {
  return useContext(ActiveOrgContext) ?? DEFENSIVE_VALUE;
}
